import React, { useEffect, useState } from "react";
import { FileText, AlertTriangle, ArrowRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../ui/alert-dialog";

const fmtEur = (n) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
};

const fetchOverdue = async () => {
    try {
        const res = await api.get("/invoices/overdue", { params: { limit: 6 } });
        return Array.isArray(res.data) ? res.data : [];
    } catch {
        return [];
    }
};

// Jun-2026 (User-Wunsch) — kompakte Rechnungs-Übersicht für den Cockpit-Bereich
// „Rechnungen": Entwürfe + überfällige Rechnungen auf einen Blick, plus Mini-Liste
// der überfälligsten offenen Posten mit „Mahnung senden" direkt im Cockpit.
export default function InvoicesOverviewCard({ kpis }) {
    const drafts = kpis?.invoices?.drafts || 0;
    const overdue = kpis?.invoices?.overdue || 0;
    const open = (url) => window.open(url, "_blank", "noopener,noreferrer");

    const [rows, setRows] = useState(null); // null = laden
    const [sendingId, setSendingId] = useState(null);
    const [confirmRow, setConfirmRow] = useState(null); // Mahnung-Bestätigung
    const [note, setNote] = useState("");
    const [sendEmail, setSendEmail] = useState(true);

    useEffect(() => {
        let active = true;
        fetchOverdue().then((d) => { if (active) setRows(d); });
        return () => { active = false; };
    }, []);

    const sendReminder = async (row) => {
        setSendingId(row.id);
        try {
            const res = await api.post(`/invoices/${row.id}/reminder`, {
                send_email: sendEmail,
                note: note.trim() || null,
            });
            const lvl = res?.data?.reminder?.level;
            const mailed = res?.data?.email?.sent;
            toast.success(
                `Mahnung Stufe ${lvl} für ${row.invoice_number} ` +
                (mailed ? "angelegt · E-Mail an Kunde versendet" : "angelegt"),
            );
            setRows(await fetchOverdue());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mahnung fehlgeschlagen");
        } finally {
            setSendingId(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="cockpit-widget-invoices_overview">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-black text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#FFCC00]" /> Rechnungs-Übersicht
                </h3>
                <button
                    type="button"
                    onClick={() => open("/dealer/invoices")}
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#111827] hover:underline"
                    data-testid="invoices-open-all"
                >
                    Alle Rechnungen <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => open("/dealer/invoices")}
                    className="text-left p-4 rounded-sm border border-gray-200 hover:border-[#FFCC00] hover:bg-[#FFFBEB] transition-colors"
                    data-testid="invoices-stat-drafts"
                >
                    <div className="text-3xl font-mono-num font-black text-[#111827]">{drafts}</div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Entwürfe</div>
                </button>
                <button
                    type="button"
                    onClick={() => open("/dealer/invoices")}
                    className={`text-left p-4 rounded-sm border transition-colors ${overdue > 0 ? "border-red-300 bg-red-50 hover:border-red-500" : "border-gray-200 hover:border-[#FFCC00] hover:bg-[#FFFBEB]"}`}
                    data-testid="invoices-stat-overdue"
                >
                    <div className={`text-3xl font-mono-num font-black flex items-center gap-1.5 ${overdue > 0 ? "text-red-600" : "text-[#111827]"}`}>
                        {overdue}{overdue > 0 && <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Überfällig</div>
                </button>
            </div>

            {/* Mini-Liste der überfälligsten offenen Posten + Direkt-Mahnung */}
            <div className="mt-5" data-testid="invoices-overdue-list">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                    Überfällige offene Posten
                </div>

                {rows === null ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-3" data-testid="invoices-overdue-loading">
                        <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
                    </div>
                ) : rows.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-3" data-testid="invoices-overdue-empty">
                        <CheckCircle2 className="w-4 h-4 text-green-600" /> Keine überfälligen Rechnungen — top!
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {rows.map((r) => (
                            <li
                                key={r.id}
                                className="flex items-center justify-between gap-3 py-2.5"
                                data-testid={`invoices-overdue-row-${r.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm text-[#111827] truncate">{r.invoice_number}</span>
                                        {r.reminder_level > 0 && (
                                            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700">
                                                Mahnstufe {r.reminder_level}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                        {r.customer_name} · <span className="text-red-600 font-mono-num">{r.days_overdue} Tg. überfällig</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="font-mono-num font-black text-sm text-[#111827]">{fmtEur(r.open_amount)}</span>
                                    <button
                                        type="button"
                                        disabled={!r.can_remind || sendingId === r.id}
                                        onClick={() => { setNote(""); setSendEmail(true); setConfirmRow(r); }}
                                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm text-[11px] font-black uppercase tracking-wider bg-[#111827] text-[#FFCC00] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        title={r.can_remind ? `Mahnung Stufe ${r.next_level} senden` : "Höchste Mahnstufe erreicht"}
                                        data-testid={`invoices-remind-btn-${r.id}`}
                                    >
                                        {sendingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                        {r.can_remind ? `Mahnung St. ${r.next_level}` : "St. 3"}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Bestätigung mit Stufen-/Gebühren-Vorschau vor dem Senden */}
            <AlertDialog open={!!confirmRow} onOpenChange={(o) => { if (!o) setConfirmRow(null); }}>
                <AlertDialogContent data-testid="invoices-remind-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Mahnung senden?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <div>
                                    Für <span className="font-bold text-[#111827]">{confirmRow?.invoice_number}</span> an{" "}
                                    <span className="font-bold text-[#111827]">{confirmRow?.customer_name}</span> wird folgende Mahnung erstellt:
                                </div>
                                <div className="rounded-sm border border-gray-200 divide-y divide-gray-100">
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-gray-500">Mahnstufe</span>
                                        <span className="font-bold text-[#111827]" data-testid="confirm-level">Stufe {confirmRow?.next_level}</span>
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-gray-500">Mahngebühr</span>
                                        <span className="font-mono-num font-bold text-[#111827]" data-testid="confirm-fee">{fmtEur(confirmRow?.next_fee)}</span>
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-gray-500">Neue Fälligkeit</span>
                                        <span className="font-bold text-[#111827]" data-testid="confirm-due">{fmtDate(confirmRow?.next_due_date)}</span>
                                    </div>
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-gray-500">Offener Betrag</span>
                                        <span className="font-mono-num font-bold text-red-600">{fmtEur(confirmRow?.open_amount)}</span>
                                    </div>
                                </div>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={2}
                                    maxLength={500}
                                    placeholder="Interner Mahn-Kommentar (optional) …"
                                    className="w-full text-sm border border-gray-300 rounded-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#FFCC00] resize-none"
                                    data-testid="confirm-note"
                                />
                                <label className="flex items-center gap-2 text-xs font-bold text-[#111827] cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={!sendEmail}
                                        onChange={(e) => setSendEmail(!e.target.checked)}
                                        className="w-4 h-4 accent-[#111827]"
                                        data-testid="confirm-no-email"
                                    />
                                    Nur anlegen, nicht per E-Mail senden
                                </label>
                                <div className="text-xs text-gray-500">
                                    {!sendEmail
                                        ? "Die Mahnung wird nur dokumentiert (kein E-Mail-Versand)."
                                        : confirmRow?.customer_email
                                            ? <>Die Mahnung wird als PDF an <span className="font-bold">{confirmRow.customer_email}</span> versendet.</>
                                            : "Der Kunde hat keine E-Mail-Adresse — die Mahnung wird nur angelegt."}
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-[#111827] text-[#FFCC00] hover:bg-black"
                            onClick={() => { const r = confirmRow; setConfirmRow(null); if (r) sendReminder(r); }}
                            data-testid="confirm-send"
                        >
                            Mahnung senden
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
